import { safeCreateObjectURL } from "@common-services/utils";
import { theme } from "@common/theme";
import { AttachFile, AudioFile, CloseOutlined, VideoFile } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Modal, Paper, Typography } from "@mui/material";
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from "react";
import { useAssetUrl } from "@common/services/asset-url-resolver";

// Types
type SupportedImageType = typeof SUPPORTED_IMAGE_TYPES[number];

interface AttachmentPreviewProps {
  attachment?: File | null;
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
  isFromCampaigns?: boolean;
  previewImageWidth?: string;
  previewMaxHeight?: string;
  pdfPreviewHeight?: string;
  imageObjectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
  customStyle?: React.CSSProperties;
}

interface MediaPreviewProps {
  url?: string;
  type?: string;
  name?: string;
  maxHeight?: string;
  pdfPreviewHeight?: string;
  imageObjectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
}

// Constants
const SUPPORTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/bmp",
  "image/webp",
  "image/svg+xml"
] as const;

const STALE_TIME = 1000 * 60 * 60; // 1 hour

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      retry: 2,
      gcTime: STALE_TIME,
      refetchOnWindowFocus: false,
    },
  },
});

// Styles
const styles = {
  fullSize: {
    width: '100%',
    height: '100%',
  },
  centerFlex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    p: 1,
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    bgcolor: 'var(--modal-backdrop)',
    color: 'white',
    '&:hover': {
      bgcolor: 'var(--modal-backdrop)',
    },
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
  },
} as const;

// Helper functions

const isImage = (type?: string): type is SupportedImageType => 
  type ? SUPPORTED_IMAGE_TYPES.includes(type as SupportedImageType) : false;
const isVideo = (type?: string): boolean => type?.startsWith("video/") ?? false;
const isAudio = (type?: string): boolean => type?.startsWith("audio/") ?? false;
const isPdf = (type?: string): boolean => type === "application/pdf";

// Sub-components
const FileInfo = memo(({ icon, name }: { icon: JSX.Element; name?: string }) => {
  if (!name) return null;
  
  return (
    <Box sx={styles.fullSize}>
      <Paper
        elevation={0}
        sx={{
          padding: theme.spacing(1),
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          backgroundColor: 'transparent',
        }}
      >
        {icon}
        <Box component="span" sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {name}
        </Box>
      </Paper>
    </Box>
  );
});
FileInfo.displayName = 'FileInfo';

const LoadingSpinner = memo(() => (
  <Box sx={{ ...styles.centerFlex, padding: theme.spacing(2) }}>
    <CircularProgress size={24} />
  </Box>
));
LoadingSpinner.displayName = 'LoadingSpinner';

const ErrorMessage = memo(({ message }: { message: string }) => (
  <Box sx={{
    color: 'error.main',
    padding: theme.spacing(1),
    textAlign: 'center'
  }}>
    {message}
  </Box>
));
ErrorMessage.displayName = 'ErrorMessage';

export const PdfPreview = memo(({ url, maxHeight, pdfPreviewHeight }: MediaPreviewProps) => (
  <Box sx={{ ...styles.fullSize, display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
    <Box
      component="iframe"
      sx={{
        width: '100%',
        height: pdfPreviewHeight ?? '200px',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        maxHeight: maxHeight ?? '200px'
      }}
      src={url}
      title="PDF Preview"
    />
  </Box>
));
PdfPreview.displayName = 'PdfPreview';

const ImagePreview = memo(({ 
  url, 
  name, 
  onImageClick,
  imageObjectFit,
}: MediaPreviewProps & { 
  onImageClick: () => void;
}) => (
  <Box
    component="img"
    onClick={onImageClick}
    sx={{
      ...styles.fullSize,
      objectFit: imageObjectFit ?? 'cover',
      borderRadius: '4px',
      cursor: 'pointer',
    }}
    src={url}
    alt={name ?? ""}
    loading="lazy"
  />
));
ImagePreview.displayName = 'ImagePreview';

const ImageModal = memo(({ 
  isOpen, 
  onClose, 
  imageUrl, 
  imageName 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  imageUrl?: string;
  imageName?: string;
}) => (
  <Modal
    open={isOpen}
    onClose={onClose}
    aria-labelledby="image-modal"
    sx={styles.centerFlex}
  >
    <Box sx={styles.modalContent}>
      <IconButton 
        onClick={onClose} 
        sx={styles.closeButton}
        aria-label="Close modal"
      >
        <CloseOutlined />
      </IconButton>
      <img
        src={imageUrl}
        alt={imageName ?? ""}
        style={styles.modalImage}
        loading="lazy"
      />
    </Box>
  </Modal>
));
ImageModal.displayName = 'ImageModal';

const VideoPreview = memo(({ url, type, name }: MediaPreviewProps) => (
  <Box sx={{ ...styles.fullSize, ...styles.centerFlex, backgroundColor: 'var(--color-text)' }}>
    <Box
      component="video"
      sx={styles.fullSize}
      controls
      preload="metadata"
      aria-label={name ?? "Video preview"}
    >
      <source src={url} type={type} />
      Your browser does not support the video tag.
    </Box>
    <Box sx={{
      position: 'absolute',
      inset: 0,
      ...styles.centerFlex,
      backgroundColor: 'rgba(0,0,0,0.3)',
      pointerEvents: 'none',
    }}>
      <VideoFile sx={{ color: 'white', fontSize: '24px' }} />
    </Box>
    {name && (
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '2px 4px',
        backgroundColor: 'rgba(0,0,0,0.5)',
        color: 'white',
        fontSize: '8px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {name}
      </Box>
    )}
  </Box>
));
VideoPreview.displayName = 'VideoPreview';

export const AudioPreview = memo(({ url, name }: MediaPreviewProps) => (
  <Box sx={{ ...styles.fullSize, backgroundColor: 'rgba(0,0,0,0.05)', padding: theme.spacing(1) }}>
    {url ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
          <AudioFile sx={{ fontSize: '16px', color: 'rgba(0,0,0,0.7)', flexShrink: 0 }} />
          {name && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '8px',
                color: 'rgba(0,0,0,0.7)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
              }}
            >
              {name}
            </Typography>
          )}
        </Box>
        <Box
          component="audio"
          sx={{
            width: '100%',
            height: '24px',
            '& audio': {
              width: '100%',
              height: '24px',
            }
          }}
          controls
          preload="metadata"
          aria-label={name ?? "Audio preview"}
        >
          <source src={url} />
          Your browser does not support the audio element.
        </Box>
      </Box>
    ) : (
      <Box sx={{ ...styles.centerFlex, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <AudioFile sx={{ fontSize: '24px', color: 'rgba(0,0,0,0.7)' }} />
          {name && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '8px',
                color: 'rgba(0,0,0,0.7)',
                textAlign: 'center',
                padding: '0 4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {name}
            </Typography>
          )}
        </Box>
      </Box>
    )}
  </Box>
));
AudioPreview.displayName = 'AudioPreview';

// Main component
export const AttachmentPreviewInner = memo((props: AttachmentPreviewProps) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const fileUrl = useMemo(
    () => props.attachment ? safeCreateObjectURL(props.attachment) : undefined,
    [props.attachment]
  );

  // Use the new asset resolver for URLs (only when no file is provided)
  const shouldResolveUrl = !!props.attachmentUrl && !fileUrl;
  const { resolvedUrl, isLoading, error } = useAssetUrl(shouldResolveUrl ? props.attachmentUrl! : '');

  const attachmentUrl = useMemo(
    () => fileUrl ?? resolvedUrl,
    [fileUrl, resolvedUrl]
  );

  const attachmentType = useMemo(
    () => props.attachment?.type ?? props.attachmentType,
    [props.attachment?.type, props.attachmentType]
  );

  const attachmentName = useMemo(
    () => props.attachmentName ?? props.attachment?.name,
    [props.attachmentName, props.attachment?.name]
  );

  const handleImageClick = useCallback(() => {
    setIsImageModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsImageModalOpen(false);
  }, []);

  const renderAttachmentPreview = useMemo(() => {
    if (shouldResolveUrl && isLoading) return <LoadingSpinner />;
    if (shouldResolveUrl && error) return <ErrorMessage message={error} />;
    if (!attachmentUrl) return null;

    const previewProps = {
      url: attachmentUrl,
      type: attachmentType,
      name: attachmentName,
      maxHeight: props.previewMaxHeight,
      pdfPreviewHeight: props.pdfPreviewHeight,
      imageObjectFit: props.imageObjectFit,
    };

    if (isPdf(attachmentType)) {
      return <PdfPreview {...previewProps} />;
    }

    if (isImage(attachmentType)) {
      return (
        <>
          <ImagePreview 
            {...previewProps}
            onImageClick={handleImageClick} 
          />
          <ImageModal 
            isOpen={isImageModalOpen}
            onClose={handleModalClose}
            imageUrl={attachmentUrl}
            imageName={attachmentName}
          />
        </>
      );
    }

    if (isVideo(attachmentType)) {
      return <VideoPreview {...previewProps} />;
    }

    if (isAudio(attachmentType)) {
      return <AudioPreview {...previewProps} />;
    }

    return <FileInfo icon={<AttachFile />} name={attachmentName} />;
  }, [
    shouldResolveUrl,
    isLoading,
    error,
    attachmentUrl,
    attachmentType,
    attachmentName,
    props.previewMaxHeight,
    props.pdfPreviewHeight,
    props.imageObjectFit,
    isImageModalOpen,
    handleImageClick,
    handleModalClose
  ]);

  if (!renderAttachmentPreview) return null;

  return (
    <Box
      sx={{
        ...styles.fullSize,
        position: 'relative',
        ...props.customStyle,
      }}
    >
      {renderAttachmentPreview}
    </Box>
  );
});
AttachmentPreviewInner.displayName = 'AttachmentPreviewInner';

// Wrapper component with QueryClientProvider
export const AttachmentPreview = memo((props: AttachmentPreviewProps) => (
  <QueryClientProvider client={queryClient}>
    <AttachmentPreviewInner {...props} />
  </QueryClientProvider>
));
AttachmentPreview.displayName = 'AttachmentPreview';

export default AttachmentPreview;